<template>
  <div
    ref           = "track"
    class         = "track"
    @click.exact  = "onJump"
    @click.alt    = "onAdvanceIndexing"
    @mouseenter   = "showCursor = true"
    @mousemove    = "onCursorMove"
    @mouseleave   = "showCursor = false"
  >

    <!-- Indexer Progress -->
    <div
      class   = "indexed-progress"
      :style  = "indexedStyle"
    >
    </div>

    <div
      class   = "not-indexed-progress"
      :style  = "notIndexedStyle"
    >
    </div>

    <!-- Discontinuities -->
    <div
      v-for   = "discontinuity in discontinuityStyles"
      :key    = "discontinuity.start"
      class   = "discontinuity"
      :style  = "discontinuity"
    >
    </div>

    <!-- Buffered Segments -->
    <div
      v-for   = "(segment, index) in bufferedSegments"
      :key    = "index"
      class   = "buffered-segment"
      :style  = "{ left: segment.left + 'px', width: segment.right - segment.left + 'px' }"
    >
    </div>

    <!-- First Timecode Label -->
    <ScrubBarLabel
      v-if        = "firstTimecode"
      class       = "first label"
      :label      = "firstTimecode"
    >
    </ScrubBarLabel>

    <!-- Last Timecode Label -->
    <ScrubBarLabel
      v-if        = "lastTimecode"
      class       = "last label"
      :label      = "lastTimecode"
    >
    </ScrubBarLabel>

    <!-- Cursor Timecode Label -->
    <ScrubBarLabel
      v-if        = "cursorTimecode"
      class       = "cursor label"
      :style      = "cursorTimecodeStyle"
      background  = "#151515"
      :label      = "cursorTimecode"
      :invisible  = "!isCursorShown"
    >
    </ScrubBarLabel>

    <!-- In/Out Marker Timecode Label -->
    <ScrubBarLabel
      ref         = "inOutLabel"
      class       = "label"
      :style      = "inOutLabelStyle"
      background  = "#151515"
      border      = "yellow"
      removeable
      :label      = "markerTimecode"
      :invisible  = "!isInOutLabelShown"
      @enter      = "onKeepMarkerLabel();"
      @leave      = "onHideMarkerLabels();"
      @remove     = "onRemoveMarker();"
    >
    </ScrubBarLabel>

    <!-- In Marker -->
    <ScrubBarMarker
      ref         = "inMarker"
      v-if        = "video.inMarker"
      class       = "marker"
      color       = "yellow"
      type        = "arrow_right"
      :style      = "inMarkerStyle"
      @enter      = "onShowMarkerLabel('in');"
      @leave      = "onHideMarkerLabels();"
    >
    </ScrubBarMarker>

    <!-- Out Marker -->
    <ScrubBarMarker
      ref         = "outMarker"
      v-if        = "video.outMarker"
      class       = "marker"
      color       = "yellow"
      type        = "arrow_left"
      :style      = "outMarkerStyle"
      @enter      = "onShowMarkerLabel('out');"
      @leave      = "onHideMarkerLabels();"
    >
    </ScrubBarMarker>

    <!-- Scrubber -->
    <div
      class       = "scrubber elevation-6"
      :class      = "{ scrubbing: isScrubbing }"
      :style      = "scrubberStyle"
      @mousedown  = "onScrubbingStarted"
    >
    </div>

  </div>
</template>

<script lang="js">

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import { ToFrames } from '@user-interface/timecode-utilities';
import {
  ADVANCE_INDEXING,
  CLEAR_MARK_IN,
  CLEAR_MARK_OUT,
  JUMP_TO_TIME,
  PAUSE_VIDEO,
  PLAY_VIDEO
} from '@/utils/action-types';
import { getTimecode } from '@/store/utilities/timecodes.utils';
import { SecondsToLongTime } from '@/utils/timeline-utilities';
import ScrubBarMarker from './scrubbar/ScrubBarMarker';
import ScrubBarLabel from './scrubbar/ScrubBarLabel';

// #endregion Imports

// #region Constants

const SCRUBBER_WIDTH            = 14;
const MARKER_LABEL_MIN_DISTANCE = 4;

// #endregion Constants

export default {

  // #region Properties

  name: 'ScrubBar',
  data() {
    return {
      isScrubbing:         false,
      playAfterScrubbing:  false,
      relativeScrubOffset: 0,
      cursorOffset:        0,
      showCursor:          0,
      trackWidth:          0,
      marker:              undefined,
      isInOutLabelShown:   false,
      markerTimeoutHandle: undefined,
      sizeObserver:        undefined
    };
  },
  computed: {
    ...mapState([
      'videoController',
      'video',
      'indexer',
      'timeDisplaySource',
      'videoOpen'
    ]),
    scrubberStyle() {
      return {
        transform: `translateX(${(this.relativeScrubOffset * this.trackWidth) - (SCRUBBER_WIDTH / 2)}px)`
      };
    },
    indexedStyle() {
      return {
        width: `${Math.min((this.indexerProgress / this.video.duration) * 100, 100)}%`
      };
    },
    notIndexedStyle() {
      return {
        width: `${Math.max(100 - ((this.indexerProgress / this.video.duration) * 100), 0)}%`,
      };
    },
    discontinuityStyles() {
      return this.indexerDiscontinuities.map(d => {
        return {
          transform: `translateX(${((d.start / this.video.duration) * this.trackWidth)}px)`,
          width:     `${((d.end - d.start) / this.video.duration) * 100}%`
        };
      });
    },
    inMarkerStyle() {
      let width = 0;
      if (this.$refs.inMarker) {
        width = this.$refs.inMarker.$el.clientWidth;
      }

      return {
        'transform': `translateX(${((this.video.inMarker / this.video.duration) * this.trackWidth) - (width / 2) - 1 }px)`
      };
    },
    outMarkerStyle() {
      let width = 0;
      if (this.$refs.outMarker) {
        width = this.$refs.outMarker.$el.clientWidth;
      }

      return {
        'transform': `translateX(${((this.video.outMarker / this.video.duration) * this.trackWidth) - (width / 2) - 1 }px)`
      };
    },
    inOutLabelStyle() {
      let width = 0;
      if (this.$refs.inOutLabel) {
        width = this.$refs.inOutLabel.$el.clientWidth;
      }

      const time = (this.marker === 'in') ? this.video.inMarker : this.video.outMarker;
      let offset = ((time / this.video.duration) * this.trackWidth) - (width / 2);
      offset = Math.max(MARKER_LABEL_MIN_DISTANCE, offset);
      offset = Math.min(this.trackWidth - MARKER_LABEL_MIN_DISTANCE - width, offset);

      return {
        'transform':      `translateX(${offset}px)`,
        'pointer-events': this.isInOutLabelShown ? 'initial' : 'none'
      };
    },
    cursorTimecodeStyle() {
      return {
        'transform': `translateX(${this.cursorOffset + 20}px)`
      }
    },
    firstTimecode() {
      switch (this.timeDisplaySource.type) {

      case 'seconds': return '00:00:00.000';
      case 'frames': return '0';
      default:
      case 'timecode': {
        if (!this.video.firstTimecode) {
          return undefined;
        }

        return this.video.firstTimecode.split('@')[0];
      }

      }
    },
    lastTimecode() {
      switch (this.timeDisplaySource.type) {
      case 'seconds': return SecondsToLongTime(this.video.duration);
      case 'frames': {
        if (!this.video.lastTimecode) {
          '';
        }
          
        return ToFrames(this.video.lastTimecode).toString();
      }
      default:
      case 'timecode': {
        if (!this.video.lastTimecode) {
          return undefined;
        }

        return this.video.lastTimecode.split('@')[0];
      }
      }
    },
    markerTimecode() {
      const time = ((this.marker === 'in') ? this.video.inMarker : this.video.outMarker) || 0;
      const timecode = getTimecode(this.video.element, time, this.timeDisplaySource.name);

      switch (this.timeDisplaySource.type) {
      case 'seconds': return SecondsToLongTime(time);
      case 'frames': {
        if (!timecode) {
          return '';
        }

        return ToFrames(timecode).toString();
      }
      default:
      case 'timecode': {
        if (!timecode) {
          return '';
        }

        return timecode.split('@')[0];
      }
      }

      /*
      if (time === undefined) {
        return '00:00:00:00';
      }

      const timecode = getTimecode(this.video.element, time, this.timeDisplaySource.name);
      if (!timecode) {
        return;
      }

      return timecode.split('@')[0];
      */
    },
    cursorTimecode() {
      if (!this.video.canPlay) {
        return undefined;
      }

      const cursorTime = (this.cursorOffset / this.trackWidth) * this.video.duration;
      const timecode = getTimecode(this.video.element, cursorTime, this.timeDisplaySource.name);

      switch (this.timeDisplaySource.type) {

      case 'seconds': return SecondsToLongTime(cursorTime);
      case 'frames': {
        if (timecode) {
          return ToFrames(timecode).toString();
        } else {
          return '';
        }
      }
      default:
      case 'timecode': {    
        if (timecode) {
          return timecode.split('@')[0];
        } else {
          return '';
        }
      }

      }
    },
    isCursorShown() {
      return (this.showCursor && !this.isInOutLabelShown) || this.isScrubbing;
    },
    bufferedSegments() {
      if (!this.videoController) {
        return [];
      }

      return this.videoController.buffered.map(s => this.calculateSegmentOffset(s.start, s.end));
    },
    indexerProgress() {
      if (!this.indexer.progress) {
        return 0;
      }

      return this.indexer.progress.progress;
    },
    indexerDiscontinuities() {
      if (!this.indexer.progress) {
        return [];
      }

      return this.indexer.progress.discontinuities;
    },
    isOpenFile() {
      return (this.videoOpen ? true : false);
    }
  },
  watch: 
  {
    'video.currentTime'() {
      this.updateScrubOffset();
    },
    'video.duration'() {
      this.updateScrubOffset();
    }
  },
  components: {
    ScrubBarMarker,
    ScrubBarLabel
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.trackWidth = this.$refs.track.offsetWidth;
    this.sizeObserver = new ResizeObserver(this.onTrackResized).observe(this.$refs.track);
  },

  beforeDestroy() {
    if (this.sizeObserver) {
      this.sizeObserver.unobserve(this.$refs.track);
    }
  },

  // #endregion Life-Cycle

  // #region Events

  methods: {

    // #region UI Events

    onJump(event) {
      this.relativeScrubOffset = this.calculateEventOffset(event);
      this.$store.dispatch(JUMP_TO_TIME, this.relativeScrubOffset * this.video.duration);
    },

    onAdvanceIndexing(event) {
      const offset = this.calculateEventOffset(event);
      this.$store.dispatch(ADVANCE_INDEXING, { isOpenFile: this.isOpenFile, percentage: offset });
    },

    onCursorMove(event) {
      const trackBounds = this.$refs.track.getBoundingClientRect();
      this.cursorOffset = event.clientX - trackBounds.x;
    },

    onScrubbingStarted(event) {
      this.relativeScrubOffset = this.calculateEventOffset(event);
      this.isScrubbing = true;

      window.addEventListener('mouseup', this.onScrubbingEnded);
      window.addEventListener('mousemove', this.onScrubbing);
      document.body.style.cursor = 'grabbing';

      this.$store.dispatch(PAUSE_VIDEO);
      this.playAfterScrubbing = !this.video.paused;
    },

    onScrubbing(event) {
      if (!this.isScrubbing) {
        return;
      }

      event.preventDefault();

      this.relativeScrubOffset = this.calculateEventOffset(event);
      this.cursorOffset = this.relativeScrubOffset * this.trackWidth;
      this.$store.dispatch(JUMP_TO_TIME, this.relativeScrubOffset * this.video.duration);
    },

    onScrubbingEnded() {
      if (!this.isScrubbing) {
        return;
      }

      this.isScrubbing = false;
      window.removeEventListener('mousemove', this.onScrubbing);
      window.removeEventListener('mouseup', this.onScrubbingEnded);
      document.body.style.cursor = 'default';

      if (this.playAfterScrubbing) {
        this.$store.dispatch(PLAY_VIDEO);
      }
    },

    onTrackResized() {
      if (this.$refs.track) {
        this.trackWidth = this.$refs.track.offsetWidth;
      }
    },

    onShowMarkerLabel(marker) {
      clearTimeout(this.markerTimeoutHandle);

      this.isInOutLabelShown = true;
      this.marker = marker;
    },

    onHideMarkerLabels() {
      clearTimeout(this.markerTimeoutHandle);

      this.markerTimeoutHandle = setTimeout(
        () => {
          this.isInOutLabelShown = false;
        },
        300
      );
    },

    onKeepMarkerLabel() {
      clearTimeout(this.markerTimeoutHandle);
    },

    onRemoveMarker() {
      if (this.marker === 'in') {
        this.$store.dispatch(CLEAR_MARK_IN);
      } else if (this.marker === 'out') {
        this.$store.dispatch(CLEAR_MARK_OUT);
      }

      this.isInOutLabelShown = false;
    },

    // #endregion UI Events

    // #region Utilities

    calculateEventOffset(event) {
      const trackBounds = this.$refs.track.getBoundingClientRect();
      const absoluteLeftOffset = event.clientX - trackBounds.x;
      const relativeLeftOffset = absoluteLeftOffset / trackBounds.width;
      return Math.max(0, Math.min(relativeLeftOffset, 1));
    },

    calculateSegmentOffset(start, end) {
      const relativeLeftOffset = start / this.video.duration;
      const relativeRightOffset = end / this.video.duration;

      return {
        left:  relativeLeftOffset * this.trackWidth,
        right: relativeRightOffset * this.trackWidth
      };
    },

    updateScrubOffset() {
      /*
       * To make the scrubber not jump around after scrubbing and clicking on the
       * track, this component generally will only use the external value after
       * it changes.
       */
      if (!this.scrubbing) {
        this.relativeScrubOffset = (this.video.currentTime /  this.video.duration);
      }
    }

    // #endregion Utilities

  }

  // #endregion Events

}
</script>

<style scoped lang="scss">

$track-color:         rgba(#000000, 0.3);
$indexed-color:       rgba(#0099ee, 0.3);
$buffered-color:      rgba(#0099ee, 0.6);
$discontinuity-color: #ff5252;

.track {
  height:           6px;
  background-color: $track-color;
  cursor:           pointer;

  .indexed-progress {
    position:     absolute;
    bottom:       0;
    top:          0;
    background:   $indexed-color;
  }

  .not-indexed-progress {
    position:     absolute;
    bottom:       0;
    top:          0;
    right:        0;
    background:   repeating-linear-gradient(
      30deg,
      $indexed-color,
      $indexed-color 1px,
      $track-color 1px,
      $track-color 3px
    );
  }

  .discontinuity {
    position:     absolute;
    bottom:       0;
    top:          0;
    background:   repeating-linear-gradient(
      30deg,
      $discontinuity-color,
      $discontinuity-color 1px,
      transparent 1px,
      transparent 3px
    );
  }

  .buffered-segment {
    background-color: $buffered-color;

    position: absolute;
    top: 0;
    bottom: 0;
  }

  .label {
    position:       absolute;
    top:            -36px;
    z-index:        5;

    &.first {
      pointer-events: none;
      left:           4px;
    }

    &.last {
      pointer-events: none;
      right:          4px;
    }

    &.cursor {
      top:            14px;
    }
  }

  .marker {
    position: absolute;
    bottom:   0;
  }

  .scrubber {
    background-color: #0099ee;
    border-radius:    9999px;
    
    width:            14px;
    height:           14px;

    position:         absolute;
    top:              -4px;

    cursor:             grab;
    &.scrubbing {
      cursor:             grabbing;
    }
  }
}
</style>
